<template>
  <div class="min-h-screen bg-gray-100 dark:bg-gray-900">
    <!-- Sidebar -->
    <Sidebar 
      v-model:isOpen="isSidebarOpen" 
      v-model:isCollapsed="isSidebarCollapsed"
    />

    <div 
      class="transition-all duration-300 ease-in-out"
      :class="{
        'lg:pl-64': !isSidebarCollapsed,
        'lg:pl-20': isSidebarCollapsed
      }"
    >
      <!-- Header -->
      <header class="sticky top-0 z-30 flex flex-col flex-shrink-0 bg-white dark:bg-gray-800 shadow-xs border-b border-gray-200/80 dark:border-gray-700">
        <!-- Top Brand Accent Line (Forest Green -> Royal Blue -> Golden Yellow) -->
        <div class="h-1 w-full bg-gradient-to-r from-[#0b6623] via-[#0275b1] to-[#ffd62e]"></div>

        <div class="flex h-15 items-center justify-between px-4">
          <!-- Mobile Menu Button -->
          <button
            type="button"
            class="px-2 text-gray-500 dark:text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#0b6623] lg:hidden"
            @click="toggleSidebar"
          >
            <span class="sr-only">Open sidebar</span>
            <Menu class="h-6 w-6" />
          </button>

          <!-- Header Content -->
          <div class="flex flex-1 items-center justify-between px-2 sm:px-4">
            <div class="flex items-center gap-3">
              <!-- Desktop Sidebar Collapse Button -->
              <button
                type="button"
                class="hidden lg:flex items-center justify-center w-9 h-9 text-gray-500 dark:text-gray-400 hover:text-[#0b6623] dark:hover:text-emerald-300 hover:bg-[#eefaf2] dark:hover:bg-gray-700 rounded-lg transition-all duration-200 focus:outline-none"
                @click="toggleSidebarCollapse"
              >
                <span class="sr-only">{{ isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar' }}</span>
                <PanelLeftOpen v-if="isSidebarCollapsed" class="h-5 w-5" />
                <PanelLeftClose v-else class="h-5 w-5" />
              </button>

              <!-- Website Center Contact Info (matches website header) -->
              <div class="hidden md:flex items-center gap-3 text-xs pl-2 border-l border-gray-200 dark:border-gray-700">
                <a href="tel:240-206-9131" class="flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-[#0b6623] transition-colors">
                  <span class="font-bold text-[#0b6623] dark:text-emerald-400">Bowie:</span>
                  <span>240-206-9131</span>
                </a>
                <span class="text-gray-300 dark:text-gray-600">|</span>
                <a href="tel:301-868-7597" class="flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-[#0b6623] transition-colors">
                  <span class="font-bold text-[#0b6623] dark:text-emerald-400">Clinton:</span>
                  <span>301-868-7597</span>
                </a>
              </div>
            </div>

            <!-- User Profile & Action Section -->
            <div class="ml-auto flex items-center gap-3 md:ml-6">
              <!-- Application Form Link Button -->
              <router-link
                to="/apply"
                target="_blank"
                class="hidden xl:inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-[#ffd62e] hover:bg-[#e6be19] text-[#1a1a1a] shadow-2xs transition"
              >
                <span>Application Form</span>
              </router-link>

              <!-- Document Expiration Notification Bell -->
              <DocumentExpirationNotification />

              <!-- Profile Button -->
              <div class="relative ml-1">
                <div>
                  <button
                    type="button"
                    class="flex max-w-xs items-center rounded-full bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-[#0b6623] focus:ring-offset-2"
                    @click="toggleProfile"
                  >
                    <span class="sr-only">Open user menu</span>
                    <div
                      class="h-9 w-9 rounded-full overflow-hidden bg-[#0275b1] ring-2 ring-[#ffd62e] flex items-center justify-center text-white font-bold text-xs shadow-xs"
                    >
                      <img
                        v-if="userImage"
                        :src="userImage"
                        :alt="userName"
                        class="w-full h-full object-cover"
                      />
                      <span v-else>{{ userInitials }}</span>
                    </div>
                  </button>
                </div>

              <!-- Profile Dropdown Menu -->
              <div
                v-if="isProfileOpen"
                class="absolute right-0 z-50 mt-2 w-64 origin-top-right rounded-xl bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                role="menu"
              >
                <!-- User Info -->
                <div class="px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200">
                  <div class="flex items-center justify-between gap-2">
                    <p class="font-bold text-gray-900 dark:text-white truncate" :title="userName">{{ userName }}</p>
                    <span 
                      v-if="userRoleLabel" 
                      class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap flex-shrink-0"
                      :class="userRoleLabel === 'Owner Access' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800/40' : 'bg-secondary/10 text-secondary dark:bg-secondary-dark/20 dark:text-secondary-dark-text border border-secondary/20'"
                    >
                      {{ userRoleLabel }}
                    </span>
                  </div>
                  <p class="text-xs text-gray-500 dark:text-gray-400 truncate mt-1" :title="userEmail">
                    {{ userEmail }}
                  </p>
                </div>
                <div class="border-t border-gray-100 dark:border-gray-700"></div>

                <!-- Theme Toggle -->
                <button
                  type="button"
                  class="block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                  @click="toggleTheme"
                >
                  <div class="flex items-center">
                    <component 
                      :is="isDarkMode ? Sun : Moon" 
                      class="mr-3 h-5 w-5 text-gray-400 flex-shrink-0"
                    />
                    <span class="truncate">
                      {{ isDarkMode ? 'Light Mode' : 'Dark Mode' }}
                    </span>
                  </div>
                </button>

                <!-- My Profile Button -->
                <router-link
                  to="/profile"
                  class="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                  :class="{ 'bg-gray-100 dark:bg-gray-700': isProfileActive }"
                  @click="isProfileOpen = false"
                >
                  <div class="flex items-center">
                    <User class="mr-3 h-5 w-5 text-gray-400" />
                    <span>My Profile</span>
                    <span
                      v-if="isProfileActive"
                      class="ml-auto h-2 w-2 rounded-full bg-indigo-500"
                      aria-hidden="true"
                    ></span>
                  </div>
                </router-link>

                <!-- Logout Button -->
                <button
                  type="button"
                  class="block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  @click="handleLogout"
                  :disabled="isLoading"
                >
                  <div class="flex items-center">
                    <LogOut class="mr-3 h-5 w-5 text-gray-400 flex-shrink-0" />
                    <span class="truncate">{{ isLoading ? 'Logging out...' : 'Sign out' }}</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>

      <!-- Main Content -->
      <main class="py-6">
        <div class="mx-auto max-w-8xl px-4 sm:px-6 lg:px-8">
          <router-view></router-view>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
// Imports
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useThemeStore } from '@/stores/theme'
import { useClickOutside } from '@/composables/ui/useClickOutside'
import { useUser } from '@/composables/auth/useUser'
import { useLogin } from '@/composables/auth/useLogin'
import Sidebar from './Sidebar.vue'
import DocumentExpirationNotification from './DocumentExpirationNotification.vue'
import { Menu, User, LogOut, Sun, Moon, PanelLeftClose, PanelLeftOpen } from 'lucide-vue-next'

// Store Instances
const themeStore = useThemeStore()
const route = useRoute()
const { userInitials, userName, userEmail, userImage, userRoleLabel } = useUser()
const { handleLogout, loading: isLoading } = useLogin()

// State
const isProfileOpen = ref(false)
const isSidebarOpen = ref(false)
const isSidebarCollapsed = ref(localStorage.getItem('sidebarCollapsed') === 'true')

// Computed Properties
const isProfileActive = computed(() => route.path === '/profile')
const isDarkMode = computed(() => themeStore.isDarkMode)

// Methods
const toggleProfile = () => {
  isProfileOpen.value = !isProfileOpen.value
}

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value
}

const toggleSidebarCollapse = () => {
  isSidebarCollapsed.value = !isSidebarCollapsed.value
  localStorage.setItem('sidebarCollapsed', isSidebarCollapsed.value.toString())
}

const toggleTheme = () => {
  themeStore.toggleTheme()
}

// Lifecycle Hooks
const { handleClickOutside } = useClickOutside(isProfileOpen)

// Watch for route changes to close sidebar on mobile
watch(
  () => route.path,
  () => {
    if (isSidebarOpen.value) {
      isSidebarOpen.value = false
    }
  }
)

onMounted(() => {
  themeStore.initializeTheme()
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
